import { AxiosResponse } from "axios";
import { ButtonSolid } from "component/button";
import dayjs, { Dayjs } from "dayjs";
import { useEffect, useState } from "react";
import { INursingCarePlan, IOption } from "../../type";
import { JP_YEAR_MONTH_FORMAT } from "constant/date";
import { formatJapaneseDate } from "utils";
import TableSchedule from "./TableSchedule";
import usePrint from "hook/usePrint";

interface IProps {
  handleGetNursingCarePlansAsync: (
    month: Dayjs
  ) => Promise<AxiosResponse<INursingCarePlan, any>>;
  month: Dayjs;
  getServiceOptions: () => Promise<any[]>;
  getServiceTypeOptions: () => Promise<any[]>;
  getTreatmentOptions: () => Promise<any[]>;
  patientName: string;
  isNormal: boolean;
}

const NursingCarePlanPDF = ({
  handleGetNursingCarePlansAsync,
  getServiceOptions,
  getServiceTypeOptions,
  getTreatmentOptions,
  month,
  patientName,
  isNormal,
}: IProps) => {
  const [nursingCarePlan, setNursingCarePlan] =
    useState<INursingCarePlan | null>(null);

  const { ref, handlePrint } = usePrint(true);

  const [serviceOptions, setServiceOptions] = useState<IOption[]>([]);
  const [serviceTypeOptions, setServiceTypeOptions] = useState<IOption[]>([]);
  const [treatmentOptions, setTreatmentOptions] = useState<IOption[]>([]);
  const [isReallyPrint, setIsReallyPrint] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleGetNursingCarePlans = async () => {
    try {
      setLoading(true);

      const [
        nursingCarePlanResponse,
        serviceOptionsResponse,
        serviceTypeOptionsResponse,
        treatmentOptionsResponse,
      ] = await Promise.all([
        handleGetNursingCarePlansAsync(month),
        getServiceOptions(),
        getServiceTypeOptions(),
        getTreatmentOptions(),
      ]);

      setNursingCarePlan(nursingCarePlanResponse.data);
      setServiceOptions(serviceOptionsResponse);
      setServiceTypeOptions(serviceTypeOptionsResponse);
      setTreatmentOptions(treatmentOptionsResponse);
    } catch (error) {
      console.error("Failed to get nursing care plans:", error);
    } finally {
      setIsReallyPrint(true);
      setLoading(false);
    }
  };

  const renderTitle = (title: string) => {
    return <div className="font-bold mb-1">{title}</div>;
  };

  const renderRow = ({
    label,
    value,
    isTextArea,
    isBreakInside,
  }: {
    label: string;
    value?: string;
    isTextArea?: boolean;
    isBreakInside?: boolean;
  }) => {
    return (
      <>
        <div className="font-medium text-sm whitespace-nowrap">
          {label || ""}:
        </div>

        <div
          className={`text-sm ${
            isBreakInside ? "break-inside" : ""
          } ${isTextArea ? "whitespace-pre-line" : ""}`}
        >
          {value || ""}
        </div>
      </>
    );
  };

  const renderCarePlanSummarySection = () => {
    return (
      <div>
        {renderTitle("■介護")}

        <div className="grid grid-cols-[max-content_1fr] gap-1 p-2 border">
          {renderRow({
            label: "総合的な援助の方針",
            value: nursingCarePlan?.comprehensive_aid_policy,
            isTextArea: true,
          })}

          {renderRow({
            label: "本人及び家族の意向",
            value: nursingCarePlan?.individual_family_intention,
            isTextArea: true,
          })}

          {renderRow({
            label: "援助、目的",
            value: nursingCarePlan?.aid_purpose,
            isTextArea: true,
          })}
        </div>
      </div>
    );
  };

  const renderCarePlanGoals = (
    name: string,
    type: "long" | "short" = "long"
  ) => {
    const dataRenderPlan =
      type === "long"
        ? nursingCarePlan?.plan_long_terms
        : nursingCarePlan?.plan_short_terms;

    let startDate = "";
    let endDate = "";

    if (type === "long") {
      startDate =
        (nursingCarePlan?.start_long_term &&
          formatJapaneseDate(dayjs(nursingCarePlan.start_long_term))) ||
        "";

      endDate =
        (nursingCarePlan?.end_long_term &&
          formatJapaneseDate(dayjs(nursingCarePlan.end_long_term))) ||
        "";
    } else {
      startDate =
        (nursingCarePlan?.start_short_term &&
          formatJapaneseDate(dayjs(nursingCarePlan.start_short_term))) ||
        "";

      endDate =
        (nursingCarePlan?.end_short_term &&
          formatJapaneseDate(dayjs(nursingCarePlan.end_short_term))) ||
        "";
    }

    return (
      <div className="flex-1">
        <div>
          {renderTitle(`◯${name || ""}`)}

          <div className="grid grid-cols-[max-content_1fr] gap-1 p-2 border">
            {renderRow({
              label: "期間",
              value: `${startDate}-${endDate}`,
            })}

            {dataRenderPlan?.map((item, index) =>
              renderRow({
                label: `${name}${index + 1}`,
                value: item.term_goal,
              })
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderLongTermGoalsSection = () => {
    return (
      <div>
        <div className="flex gap-2">
          {renderCarePlanGoals("長期目標", "long")}
          {renderCarePlanGoals("短期目標", "short")}
        </div>
      </div>
    );
  };

  const renderSchedulesSection = () => {
    return (
      <div>
        {renderTitle(`◯予定表`)}

        {nursingCarePlan && (
          <TableSchedule
            nursingCarePlan={nursingCarePlan}
            serviceOptions={serviceOptions}
            serviceTypeOptions={serviceTypeOptions}
          />
        )}

        <div className="flex gap-4 mt-1">
          {renderRow({
            label: "指示書",
            value: nursingCarePlan?.note,
            isTextArea: true,
          })}
        </div>
      </div>
    );
  };

  const renderTreatmentImprovementSection = () => {
    return (
      <div>
        {renderTitle(`◯処遇改善加算`)}
        <div className="grid grid-cols-[max-content_1fr] gap-1 p-2 border">
          {nursingCarePlan?.improvement_nursing_care_plans?.map((item) => {
            const itemImprovement = treatmentOptions.find(
              (treatmentOption) =>
                treatmentOption.value === item.treatment_improvement_id
            );
            return renderRow({
              label: "項目名",
              value: itemImprovement?.label,
            });
          })}
          {renderRow({
            label: "備考",
            value: nursingCarePlan?.remarks,
            isTextArea: true,
          })}
        </div>
      </div>
    );
  };

  useEffect(() => {
    if (isReallyPrint) {
      handlePrint();
      setNursingCarePlan(null);
      setIsReallyPrint(false);
    }
  }, [isReallyPrint]);

  return (
    <div>
      <ButtonSolid
        className="!h-[35px] !px-[34px]"
        onClick={handleGetNursingCarePlans}
        disabled={loading}
        isLoading={loading}
      >
        出力
      </ButtonSolid>

      <div ref={ref} hidden>
        <div className="flex mb-2 gap-3">
          <div className="flex-1 border px-1 flex justify-center whitespace-nowrap">
            <span>{patientName}</span>

            <span className="ml-1">
              {isNormal ? "ケアマネプラン(介護)" : "ケアマネプラン(障害)"}
            </span>
          </div>

          <div className="flex-1 text-center text-lg font-bold whitespace-nowrap">
            {month.format(JP_YEAR_MONTH_FORMAT)}
          </div>

          <div className="flex-1" />
        </div>

        {nursingCarePlan && (
          <div className="flex flex-col gap-5">
            <section>
              {renderCarePlanSummarySection()}
            </section>

            {isNormal && (
              <section className="break-inside">
                {renderLongTermGoalsSection()}
              </section>
            )}

            <section className="break-inside">
              {renderSchedulesSection()}
            </section>

            <section className="break-inside">
              {renderTreatmentImprovementSection()}
            </section>
          </div>
        )}
      </div>
    </div>
  );
};

export default NursingCarePlanPDF;